import React, { FC, Suspense, useLayoutEffect, useMemo, useRef } from 'react';
import { createSignal, Route } from '@funtech-int/rival';
import {
    continueWatchingQuery,
    removeContinueWatchingItemMutation,
    WatchingEntity,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { ErrorBoundary, useSignal } from '@funtech-int/rival-react';
import { useStorage } from '@funtech-int/web-toolkit';
import { useInView } from 'react-intersection-observer';

import { PageSpinner } from '@/shared/components/PageSpinner';
import { useStartPlayingContent } from '@/scenarios/legacy-player/components/player-manager/hooks/useStartPlayingContent';
import { withPlayingContentChangeRemount } from '@/scenarios/legacy-player/components/player-manager/hocs/withPlayingContentChangeRemount';

import * as Card from '../Card';
import { Carousel, CarouselHandle } from '../Carousel/Carousel';
import { useGqlQuery } from '../../hooks/useGqlQuery';
import { useGraphqlClient } from '../../hooks/useGraphqlClient';

import { SelectionTitle } from './_SelectionTitle';
import styles from './_ContinueWatching.module.css';

const HINT_CARD_POSITION = 2;
const HIDE_CONTINUE_WATCHING_HINT_KEY = 'cw_hint_hidden';
const HIDDEN_CONTINUE_WATCHING_HINT_VALUE = '1';

interface ContinueWatchingProps {
    id: string;
    showcaseId?: string;
    movieRoute: Route<any, { id: string }, any>;
    className?: string;
    onCardShow?(entity: WatchingEntity, index: number): void;
    onCardClick?(entity: WatchingEntity, index: number): void;
}

const ContinueWatchingContainer = withPlayingContentChangeRemount<ContinueWatchingProps>(
    ({ id, showcaseId, movieRoute, className, onCardShow, onCardClick }) => {
        const carouselRef = useRef<CarouselHandle>(null);
        const graphqlClient = useGraphqlClient();

        const { data: continueWatching } = useGqlQuery(
            continueWatchingQuery,
            {
                id,
                selectionOffset: 0,
                selectionLimit: 20,
                showcaseId: showcaseId ?? null,
                ignoreCache: true,
            },
            { lifetime: 0 }
        );

        const startContent = useStartPlayingContent();
        const storage = useStorage();

        const controller = useMemo(() => {
            const items = createSignal(continueWatching?.items ?? []);

            return {
                items: items.seal(),

                remove(entity: WatchingEntity) {
                    items.set(items.get().filter((item) => item !== entity));

                    graphqlClient.fetch({
                        operation: removeContinueWatchingItemMutation,
                        variables: { contentId: entity.movieId ?? entity.contentId },
                    });
                },

                shouldShowRemoveButton() {
                    return (
                        storage.local.get(HIDE_CONTINUE_WATCHING_HINT_KEY) !==
                        HIDDEN_CONTINUE_WATCHING_HINT_VALUE
                    );
                },

                shouldShowRemoveHint(index: number) {
                    return index === Math.min(items.get().length, HINT_CARD_POSITION) - 1;
                },

                onCloseHint() {
                    storage.local.set(
                        HIDE_CONTINUE_WATCHING_HINT_KEY,
                        HIDDEN_CONTINUE_WATCHING_HINT_VALUE
                    );
                },
            };
        }, [continueWatching, graphqlClient, storage]);

        const items = useSignal(controller.items);

        useLayoutEffect(() => {
            carouselRef.current?.refresh();
        }, [items.length]);

        const [inViewRef, inView] = useInView({
            threshold: 0.5,
            triggerOnce: true,
        });

        if (!continueWatching || !items.length) {
            return null;
        }

        return (
            <div className={className} data-tid="ContinueWatching" ref={inViewRef}>
                <SelectionTitle title={continueWatching.title} />
                <Carousel ref={carouselRef}>
                    {items.map((item, index) => {
                        const handleCardShow = () => {
                            onCardShow?.(item, index);
                        };

                        const handleCardClick = () => {
                            onCardClick?.(item, index);
                        };

                        const handleRemove = () => {
                            controller.remove(item);
                        };

                        return (
                            <Card.Watching
                                key={item.contentId}
                                entity={item}
                                location={movieRoute({ id: item.contentId })}
                                startContent={startContent}
                                onShow={handleCardShow}
                                onNavigated={handleCardClick}
                                onRemove={handleRemove}
                                onCloseRemoveHint={controller.onCloseHint}
                                withHint={
                                    controller.shouldShowRemoveButton() &&
                                    controller.shouldShowRemoveHint(index) &&
                                    inView
                                }
                                withVisibleRemoveButton={controller.shouldShowRemoveButton()}
                            />
                        );
                    })}
                </Carousel>
            </div>
        );
    }
);

export const ContinueWatching: FC<ContinueWatchingProps> = ({
    id,
    showcaseId,
    movieRoute,
    className,
    onCardShow,
    onCardClick,
}) => (
    <ErrorBoundary fallback={() => null}>
        <Suspense fallback={<PageSpinner className={styles.spinner} loadMore />}>
            <ContinueWatchingContainer
                id={id}
                showcaseId={showcaseId}
                movieRoute={movieRoute}
                className={className}
                onCardShow={onCardShow}
                onCardClick={onCardClick}
            />
        </Suspense>
    </ErrorBoundary>
);
